/**
 * Moves inline event handlers (onclick="..." and friends) out of widget HTML
 * into its script. Published pages strip inline handlers for safety, which
 * silently breaks pasted code that relies on them. In the script, the handler
 * code also sees the widget's own functions, which inline handlers can't.
 *
 * Works on the HTML text: only the on… attributes change, so template syntax
 * and formatting elsewhere are left exactly as written.
 */

export type MovedHandlers = {
  html: string;
  /** Script that wires the handlers up; empty when nothing moved. Expects `root`. */
  js: string;
  /** Event names moved, e.g. ['click', 'click', 'input']. */
  events: string[];
  /** Handlers left in place because they use template values like {{id}}. */
  templated: number;
};

const ENTITIES: Record<string, string> = { quot: '"', apos: '\'', amp: '&', lt: '<', gt: '>', nbsp: ' ' };

export const decodeEntities = (text: string): string => text.replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (m, code: string) => {
  if (code[0] === '#') {
    const n = code[1].toLowerCase() === 'x' ? parseInt(code.slice(2), 16) : parseInt(code.slice(1), 10);
    // Out of Unicode's range (&#x110000;) fromCodePoint throws; leave such text as written.
    return Number.isFinite(n) && n >= 0 && n <= 0x10ffff ? String.fromCodePoint(n) : m;
  }
  return Object.prototype.hasOwnProperty.call(ENTITIES, code.toLowerCase()) ? ENTITIES[code.toLowerCase()] : m;
});

type Attr = { name: string; value: string | null; start: number; end: number };

/** Parses the attributes of a start tag (text between the tag name and ">"). */
const parseAttributes = (tag: string, from: number): { attrs: Attr[]; end: number } => {
  const attrs: Attr[] = [];
  let i = from;
  while (i < tag.length) {
    while (i < tag.length && /\s/.test(tag[i])) i++;
    if (tag[i] === '>' || (tag[i] === '/' && tag[i + 1] === '>')) break;
    if (i >= tag.length) break;
    const start = i;
    while (i < tag.length && !/[\s=>]/.test(tag[i]) && !(tag[i] === '/' && tag[i + 1] === '>')) i++;
    const name = tag.slice(start, i);
    let j = i;
    while (j < tag.length && /\s/.test(tag[j])) j++;
    let value: string | null = null;
    if (tag[j] === '=') {
      j++;
      while (j < tag.length && /\s/.test(tag[j])) j++;
      const q = tag[j];
      if (q === '"' || q === '\'') {
        const close = tag.indexOf(q, j + 1);
        const stop = close === -1 ? tag.length : close;
        value = tag.slice(j + 1, stop);
        j = stop + 1;
      } else {
        const vs = j;
        while (j < tag.length && !/[\s>]/.test(tag[j])) j++;
        value = tag.slice(vs, j);
      }
      i = j;
    }
    if (!name) { i++; continue; }
    attrs.push({ name, value, start, end: i });
  }
  return { attrs, end: i };
};

const quoteJs = (s: string) => JSON.stringify(s);

export const moveInlineHandlers = (html: string): MovedHandlers => {
  const events: string[] = [];
  const wiring: string[] = [];
  let out = '';
  let i = 0;
  // Numbering continues after ids from an earlier run (data-gx-on="h1 h2"), whose wiring is
  // already in the script: reusing "h1" would bind the new handler to the old element too.
  let counter = 0;
  (html.match(/data-gx-on\s*=\s*(["'])[^"']*\1/gi) || []).forEach((attr) => {
    (attr.match(/\bh(\d+)\b/g) || []).forEach((id) => { counter = Math.max(counter, Number(id.slice(1))); });
  });
  let templated = 0;
  while (i < html.length) {
    const lt = html.indexOf('<', i);
    if (lt === -1) { out += html.slice(i); break; }
    out += html.slice(i, lt);
    if (html.startsWith('<!--', lt)) {
      const close = html.indexOf('-->', lt + 4);
      const stop = close === -1 ? html.length : close + 3;
      out += html.slice(lt, stop);
      i = stop;
      continue;
    }
    const nameMatch = html.slice(lt + 1, lt + 64).match(/^[a-zA-Z][\w:-]*/);
    if (!nameMatch) { out += '<'; i = lt + 1; continue; }
    const tagNameEnd = lt + 1 + nameMatch[0].length;
    const { attrs, end } = parseAttributes(html, tagNameEnd);
    const gt = html.indexOf('>', end);
    const tagEnd = gt === -1 ? html.length : gt + 1;
    const inline = attrs.filter(a => /^on[a-z]+$/i.test(a.name) && a.value !== null && a.value.trim());
    // A handler with {{template}} values needs them per item, which a shared script can't give it.
    const handlers = inline.filter(a => !(a.value as string).includes('{{'));
    templated += inline.length - handlers.length;
    if (!handlers.length) {
      out += html.slice(lt, tagEnd);
      i = tagEnd;
      continue;
    }
    const ids: string[] = [];
    handlers.forEach((h) => {
      counter++;
      const id = `h${counter}`;
      const type = h.name.slice(2).toLowerCase();
      ids.push(id);
      events.push(type);
      wiring.push(`  on(${quoteJs(id)}, ${quoteJs(type)}, function (event) {\n${decodeEntities(h.value as string)}\n  });`);
    });
    // Rebuild the tag: drop the handler attributes, keep everything else as written.
    let rebuilt = html.slice(lt, tagNameEnd);
    let cursor = tagNameEnd;
    handlers.forEach((h) => {
      rebuilt += html.slice(cursor, h.start).replace(/\s+$/, '');
      cursor = h.end;
    });
    rebuilt += html.slice(cursor, tagEnd);
    const marker = /(\sdata-gx-on\s*=\s*)(["'])([^"']*)\2/i;
    if (marker.test(rebuilt)) {
      rebuilt = rebuilt.replace(marker, (_m, lead, q, v) => `${lead}${q}${v} ${ids.join(' ')}${q}`);
    } else {
      rebuilt = rebuilt.replace(/\s*(\/?>)$/, (m, close) => ` data-gx-on="${ids.join(' ')}"${m.startsWith(' ') && close === '/>' ? ' ' : ''}${close}`);
    }
    out += rebuilt;
    i = tagEnd;
  }
  if (!wiring.length) return { html, js: '', events, templated };
  const js = [
    '// Genesis moved these inline event handlers here; published pages remove inline on… attributes.',
    '(function () {',
    '  function on(id, type, handler) {',
    '    root.querySelectorAll(\'[data-gx-on~="\' + id + \'"]\').forEach(function (el) {',
    '      el.addEventListener(type, function (event) {',
    '        if (handler.call(this, event) === false) { event.preventDefault(); }',
    '      });',
    '      // An image may have finished loading (or failed) before this ran.',
    '      if ((type === \'load\' || type === \'error\') && el.tagName === \'IMG\' && el.complete && (type === \'error\') === (el.naturalWidth === 0)) {',
    '        handler.call(el, new Event(type));',
    '      }',
    '    });',
    '  }',
    ...wiring,
    '})();'
  ].join('\n');
  return { html: out, js, events, templated };
};
